import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Form, Button, Alert, Badge } from 'react-bootstrap';
import { FaChartBar, FaArrowUp, FaArrowDown, FaEquals, FaFilter, FaHome } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import transactionService from '../services/transactionService';
import categoryService from '../services/categoryService';
import { CATEGORY_TYPES } from '../constants/categoryTypes';

const MonthlyComparison = () => {
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedMonth1, setSelectedMonth1] = useState('');
  const [selectedMonth2, setSelectedMonth2] = useState('');
  const [availableMonths, setAvailableMonths] = useState([]);
  const [comparisonData, setComparisonData] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (selectedMonth1 && selectedMonth2) {
      generateComparison();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth1, selectedMonth2, transactions, categories]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [transactionsData, categoriesData] = await Promise.all([
        transactionService.getAll(),
        categoryService.getAll()
      ]);

      setTransactions(transactionsData);
      setCategories(categoriesData);

      // Extrair meses disponíveis das transações
      const months = [...new Set(transactionsData.map(t => t.date.substring(0, 7)))]
        .sort((a, b) => b.localeCompare(a));
      setAvailableMonths(months);

      // Definir meses padrão (dois últimos meses)
      if (months.length >= 2) {
        setSelectedMonth1(months[1]); // Penúltimo mês
        setSelectedMonth2(months[0]); // Último mês
      }
    } catch (err) {
      setError('Erro ao carregar dados: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const generateComparison = () => {
    if (!selectedMonth1 || !selectedMonth2) return;

    const month1Transactions = transactions.filter(t => 
      t.date.startsWith(selectedMonth1)
    );
    const month2Transactions = transactions.filter(t => 
      t.date.startsWith(selectedMonth2)
    );

    const comparison = {
      month1: {
        date: selectedMonth1,
        name: formatMonthName(selectedMonth1),
        summary: calculateSummary(month1Transactions),
        byCategory: calculateByCategory(month1Transactions),
        byType: calculateByType(month1Transactions)
      },
      month2: {
        date: selectedMonth2,
        name: formatMonthName(selectedMonth2),
        summary: calculateSummary(month2Transactions),
        byCategory: calculateByCategory(month2Transactions),
        byType: calculateByType(month2Transactions)
      }
    };

    // Calcular diferenças
    comparison.differences = {
      totalIncome: comparison.month2.summary.totalIncome - comparison.month1.summary.totalIncome,
      totalExpense: comparison.month2.summary.totalExpense - comparison.month1.summary.totalExpense,
      netResult: comparison.month2.summary.netResult - comparison.month1.summary.netResult,
      byCategory: calculateCategoryDifferences(comparison.month1.byCategory, comparison.month2.byCategory),
      byType: calculateTypeDifferences(comparison.month1.byType, comparison.month2.byType)
    };

    setComparisonData(comparison);
  };

  const calculateSummary = (monthTransactions) => {
    const totalIncome = monthTransactions
      .filter(t => t.amount > 0)
      .reduce((sum, t) => sum + t.amount, 0);
    
    const totalExpense = Math.abs(monthTransactions
      .filter(t => t.amount < 0)
      .reduce((sum, t) => sum + t.amount, 0));

    return {
      totalIncome,
      totalExpense,
      netResult: totalIncome - totalExpense,
      transactionCount: monthTransactions.length
    };
  };

  const calculateByCategory = (monthTransactions) => {
    const categoryTotals = {};

    monthTransactions.forEach(transaction => {
      const categoryId = transaction.categoryId;
      if (!categoryTotals[categoryId]) {
        const category = categories.find(c => c.id === categoryId);
        categoryTotals[categoryId] = {
          id: categoryId,
          name: category?.name || 'Categoria não encontrada',
          type: category?.type || 'UNKNOWN',
          total: 0,
          count: 0
        };
      }
      categoryTotals[categoryId].total += transaction.amount;
      categoryTotals[categoryId].count++;
    });

    return Object.values(categoryTotals).sort((a, b) => Math.abs(b.total) - Math.abs(a.total));
  };

  const calculateByType = (monthTransactions) => {
    const typeGrouping = {};

    Object.entries(CATEGORY_TYPES).forEach(([key, config]) => {
      typeGrouping[key] = {
        label: config.label,
        isExpense: config.isExpense,
        total: 0,
        count: 0,
        categories: []
      };
    });

    monthTransactions.forEach(transaction => {
      const category = categories.find(c => c.id === transaction.categoryId);
      if (category && typeGrouping[category.type]) {
        typeGrouping[category.type].total += transaction.amount;
        typeGrouping[category.type].count++;
      }
    });

    return typeGrouping;
  };

  const calculateCategoryDifferences = (month1Categories, month2Categories) => {
    const allCategoryIds = [...new Set([
      ...month1Categories.map(c => c.id),
      ...month2Categories.map(c => c.id)
    ])];

    return allCategoryIds.map(categoryId => {
      const cat1 = month1Categories.find(c => c.id === categoryId) || { total: 0, count: 0, name: '', type: '' };
      const cat2 = month2Categories.find(c => c.id === categoryId) || { total: 0, count: 0, name: '', type: '' };
      
      return {
        id: categoryId,
        name: cat1.name || cat2.name,
        type: cat1.type || cat2.type,
        month1Total: cat1.total,
        month2Total: cat2.total,
        difference: cat2.total - cat1.total,
        percentChange: cat1.total !== 0 ? ((cat2.total - cat1.total) / Math.abs(cat1.total)) * 100 : 0
      };
    }).sort((a, b) => Math.abs(b.difference) - Math.abs(a.difference));
  };

  const calculateTypeDifferences = (month1Types, month2Types) => {
    return Object.keys(CATEGORY_TYPES).map(typeKey => {
      const type1 = month1Types[typeKey] || { total: 0, count: 0 };
      const type2 = month2Types[typeKey] || { total: 0, count: 0 };
      
      return {
        type: typeKey,
        label: CATEGORY_TYPES[typeKey].label,
        isExpense: CATEGORY_TYPES[typeKey].isExpense,
        month1Total: type1.total,
        month2Total: type2.total,
        difference: type2.total - type1.total,
        percentChange: type1.total !== 0 ? ((type2.total - type1.total) / Math.abs(type1.total)) * 100 : 0
      };
    }).filter(t => t.month1Total !== 0 || t.month2Total !== 0)
      .sort((a, b) => Math.abs(b.difference) - Math.abs(a.difference));
  };

  const formatMonthName = (monthStr) => {
    const [year, month] = monthStr.split('-');
    const monthNames = [
      'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
      'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
    ];
    return `${monthNames[parseInt(month) - 1]} ${year}`;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(amount);
  };

  const getTrendIcon = (difference) => {
    if (difference > 0) return <FaArrowUp className="text-success" />;
    if (difference < 0) return <FaArrowDown className="text-danger" />;
    return <FaEquals className="text-muted" />;
  };

  const getTrendBadge = (difference, isExpense = false) => {
    if (difference === 0) return <Badge bg="secondary">Sem mudança</Badge>;
    
    const isPositive = difference > 0;
    const variant = isExpense 
      ? (isPositive ? 'danger' : 'success')
      : (isPositive ? 'success' : 'danger');
    
    const text = isExpense
      ? (isPositive ? 'Aumento' : 'Redução')
      : (isPositive ? 'Aumento' : 'Redução');
    
    return <Badge bg={variant}>{text}</Badge>;
  };

  if (loading) {
    return (
      <Container>
        <div className="text-center mt-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Carregando...</span>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h1 className="display-6 mb-3">
                <FaChartBar className="me-3" />
                Comparativo Mensal
              </h1>
              <p className="text-muted">Análise comparativa detalhada entre dois meses selecionados</p>
            </div>
            <div>
              <Button as={Link} to="/reports" variant="outline-primary" className="d-flex align-items-center">
                <FaHome className="me-2" />
                Central de Relatórios
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      {error && (
        <Row className="mb-4">
          <Col>
            <Alert variant="danger">{error}</Alert>
          </Col>
        </Row>
      )}

      {/* Filtros */}
      <Row className="mb-4">
        <Col md={6}>
          <Card>
            <Card.Body>
              <h5><FaFilter className="me-2" />Selecionar Meses</h5>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Primeiro Mês</Form.Label>
                    <Form.Select
                      value={selectedMonth1}
                      onChange={(e) => setSelectedMonth1(e.target.value)}
                    >
                      <option value="">Selecione...</option>
                      {availableMonths.map(month => (
                        <option key={month} value={month}>
                          {formatMonthName(month)}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Segundo Mês</Form.Label>
                    <Form.Select
                      value={selectedMonth2}
                      onChange={(e) => setSelectedMonth2(e.target.value)}
                    >
                      <option value="">Selecione...</option>
                      {availableMonths.map(month => (
                        <option key={month} value={month}>
                          {formatMonthName(month)}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {comparisonData && (
        <>
          {/* Resumo Geral */}
          <Row className="mb-4">
            <Col>
              <Card>
                <Card.Header>
                  <h5 className="mb-0">Resumo Comparativo</h5>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={3}>
                      <div className="text-center p-3 border rounded">
                        <h6 className="text-muted">Receitas</h6>
                        <div className="d-flex justify-content-between align-items-center">
                          <span>{formatCurrency(comparisonData.month1.summary.totalIncome)}</span>
                          {getTrendIcon(comparisonData.differences.totalIncome)}
                          <span>{formatCurrency(comparisonData.month2.summary.totalIncome)}</span>
                        </div>
                        <small className="text-muted">
                          Diferença: {formatCurrency(comparisonData.differences.totalIncome)}
                        </small>
                        <div className="mt-1">
                          {getTrendBadge(comparisonData.differences.totalIncome)}
                        </div>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="text-center p-3 border rounded">
                        <h6 className="text-muted">Despesas</h6>
                        <div className="d-flex justify-content-between align-items-center">
                          <span>{formatCurrency(comparisonData.month1.summary.totalExpense)}</span>
                          {getTrendIcon(comparisonData.differences.totalExpense)}
                          <span>{formatCurrency(comparisonData.month2.summary.totalExpense)}</span>
                        </div>
                        <small className="text-muted">
                          Diferença: {formatCurrency(comparisonData.differences.totalExpense)}
                        </small>
                        <div className="mt-1">
                          {getTrendBadge(comparisonData.differences.totalExpense, true)}
                        </div>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="text-center p-3 border rounded">
                        <h6 className="text-muted">Resultado Líquido</h6>
                        <div className="d-flex justify-content-between align-items-center">
                          <span className={comparisonData.month1.summary.netResult >= 0 ? 'text-success' : 'text-danger'}>
                            {formatCurrency(comparisonData.month1.summary.netResult)}
                          </span>
                          {getTrendIcon(comparisonData.differences.netResult)}
                          <span className={comparisonData.month2.summary.netResult >= 0 ? 'text-success' : 'text-danger'}>
                            {formatCurrency(comparisonData.month2.summary.netResult)}
                          </span>
                        </div>
                        <small className="text-muted">
                          Diferença: {formatCurrency(comparisonData.differences.netResult)}
                        </small>
                        <div className="mt-1">
                          {getTrendBadge(comparisonData.differences.netResult)}
                        </div>
                      </div>
                    </Col>
                    <Col md={3}>
                      <div className="text-center p-3 border rounded">
                        <h6 className="text-muted">Transações</h6>
                        <div className="d-flex justify-content-between align-items-center">
                          <span>{comparisonData.month1.summary.transactionCount}</span>
                          {getTrendIcon(comparisonData.month2.summary.transactionCount - comparisonData.month1.summary.transactionCount)}
                          <span>{comparisonData.month2.summary.transactionCount}</span>
                        </div>
                        <small className="text-muted">
                          Diferença: {comparisonData.month2.summary.transactionCount - comparisonData.month1.summary.transactionCount}
                        </small>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Comparação por Tipo de Categoria */}
          <Row className="mb-4">
            <Col>
              <Card>
                <Card.Header>
                  <h5 className="mb-0">Comparação por Tipo de Categoria</h5>
                </Card.Header>
                <Card.Body>
                  <Table responsive striped>
                    <thead>
                      <tr>
                        <th>Tipo</th>
                        <th className="text-center">{comparisonData.month1.name}</th>
                        <th className="text-center">Tendência</th>
                        <th className="text-center">{comparisonData.month2.name}</th>
                        <th className="text-center">Diferença</th>
                        <th className="text-center">Variação %</th>
                        <th className="text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {comparisonData.differences.byType.map(typeData => (
                        <tr key={typeData.type}>
                          <td>
                            <strong>{typeData.label}</strong>
                            <br />
                            <small className="text-muted">
                              {typeData.isExpense ? 'Despesa' : 'Receita'}
                            </small>
                          </td>
                          <td className="text-center">
                            {formatCurrency(Math.abs(typeData.month1Total))}
                          </td>
                          <td className="text-center">
                            {getTrendIcon(typeData.difference)}
                          </td>
                          <td className="text-center">
                            {formatCurrency(Math.abs(typeData.month2Total))}
                          </td>
                          <td className="text-center">
                            <span className={typeData.difference >= 0 ? 'text-success' : 'text-danger'}>
                              {formatCurrency(Math.abs(typeData.difference))}
                            </span>
                          </td>
                          <td className="text-center">
                            <span className={typeData.percentChange >= 0 ? 'text-success' : 'text-danger'}>
                              {typeData.percentChange.toFixed(1)}%
                            </span>
                          </td>
                          <td className="text-center">
                            {getTrendBadge(typeData.difference, typeData.isExpense)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Maiores Variações por Categoria */}
          <Row>
            <Col>
              <Card>
                <Card.Header>
                  <h5 className="mb-0">Maiores Variações por Categoria</h5>
                </Card.Header>
                <Card.Body>
                  <Table responsive striped>
                    <thead>
                      <tr>
                        <th>Categoria</th>
                        <th className="text-center">{comparisonData.month1.name}</th>
                        <th className="text-center">Tendência</th>
                        <th className="text-center">{comparisonData.month2.name}</th>
                        <th className="text-center">Diferença</th>
                        <th className="text-center">Variação %</th>
                        <th className="text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {comparisonData.differences.byCategory.slice(0, 15).map(catData => (
                        <tr key={catData.id}>
                          <td>
                            <strong>{catData.name}</strong>
                            <br />
                            <small className="text-muted">
                              {CATEGORY_TYPES[catData.type]?.label || catData.type}
                            </small>
                          </td>
                          <td className="text-center">
                            {formatCurrency(catData.month1Total)}
                          </td>
                          <td className="text-center">
                            {getTrendIcon(catData.difference)}
                          </td>
                          <td className="text-center">
                            {formatCurrency(catData.month2Total)}
                          </td>
                          <td className="text-center">
                            <span className={catData.difference >= 0 ? 'text-success' : 'text-danger'}>
                              {formatCurrency(Math.abs(catData.difference))}
                            </span>
                          </td>
                          <td className="text-center">
                            <span className={catData.percentChange >= 0 ? 'text-success' : 'text-danger'}>
                              {catData.percentChange.toFixed(1)}%
                            </span>
                          </td>
                          <td className="text-center">
                            {getTrendBadge(catData.difference, CATEGORY_TYPES[catData.type]?.isExpense)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
};

export default MonthlyComparison; 